'use client';

import { type JSX, type ReactNode, useEffect, useState } from 'react';

interface HydrationGateProps {
    children: ReactNode;
    fallback: ReactNode;
}

/**
 * Renders `fallback` until React has mounted on the client, then swaps to
 * `children`. Used to show a skeleton while the heavy chat runtime hydrates,
 * so users don't see controls that appear ready but don't respond yet.
 *
 * Server render and the initial client render both return `fallback` (so
 * hydration is consistent). The post-mount effect flips the flag and triggers
 * a re-render with `children`.
 */
export function HydrationGate({ children, fallback }: HydrationGateProps): JSX.Element {
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot post-mount flip
        setHydrated(true);
    }, []);

    return <>{hydrated ? children : fallback}</>;
}
